<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { RsLoading } from "@/components";

import { metadataService } from "@/services";

import { Metadata } from "@/models/Metadata";

import { useCaseRouteParams } from "@/hooks/use-case-route-params";

import { useBrandStore } from "@/stores/brand-store";
import { useMessageStore } from "@/stores/message-store";
import { useMetadataStore } from "@/stores/metadata-store";
import { useCaseFileStore } from "@/stores/case-file-store";

import type DialogueIdentity from "@/models/DialogueIdentity";

withDefaults(defineProps<{ file: Metadata }>(), {  })

const routeParams = useCaseRouteParams();

const name = ref<string>("");
const selectedRole = ref<string>("");
const selectedSpeaker = ref<string>("");

const transcript = computed(() => useCaseFileStore().cachedFilesData[__props.file.fileReference]?.transcript);

const dialogueRoles = computed(() => useBrandStore().availableDialogueRolesConfig);

const dialogueIdentities = computed<{ [x: string]: DialogueIdentity | undefined }>(() => {
  try {
    return JSON.parse(__props.file.dialogueIdentities ?? "{}");
  } catch (error) {
    return {};
  }
});

const identityExists = computed(() => {
  return selectedSpeaker.value && dialogueIdentities.value && selectedSpeaker.value in dialogueIdentities.value;
});

watch(selectedSpeaker, () => {
  if (!identityExists.value) {
    selectedRole.value = "";
    name.value = "";
    return;
  }

  const identity = dialogueIdentities.value[selectedSpeaker.value];

  selectedRole.value = identity?.role || "";
  name.value = identity?.name || "";
});

watch(selectedRole, (r) => {
  if (!r) {
    name.value = "";
  }
});

async function saveIdentity(): Promise<void> {
  try {
    await saveDialogueIdentities(selectedSpeaker.value, { name: name.value, role: selectedRole.value });

    useMessageStore().updateMessage({
      message: "The identity has been saved.",
      type: "success",
      title: "Identities",
      autoHide: true,
      showReload: false,
    });
  } catch (ex) {
    console.error(ex);
    useMessageStore().updateMessage({
      message: "An error occurred while saving that identity.",
      type: "error",
      title: "Identities",
      autoHide: true,
      showReload: false,
    });
  }
}

async function deleteIdentity(): Promise<void> {
  try {
    await saveDialogueIdentities(selectedSpeaker.value);

    useMessageStore().updateMessage({
      message: "The identity has been deleted.",
      type: "error",
      title: "Identities",
      autoHide: true,
      showReload: false,
    });
  } catch (ex) {
    console.error(ex);
    useMessageStore().updateMessage({
      message: "An error occurred while deleted that identity.",
      type: "error",
      title: "Identities",
      autoHide: true,
      showReload: false,
    });
  }
}

async function saveDialogueIdentities(speaker: string, arg?: Record<"name" | "role", string>): Promise<void> {
  const speakerToRoleIndex = new Map<string, number>();
  const newDialogueIdentities = {
    ...dialogueIdentities.value,
    [speaker]: arg == null ? undefined : { ...arg, index: 0 },
  };

  for (const [, identity] of Object.entries(newDialogueIdentities).sort(([lId], [rId]) => +lId - +rId)) {
    if (identity == null) continue;

    identity.name ||= undefined;
    identity.index = (speakerToRoleIndex.get(identity.role) ?? 0) + 1;

    speakerToRoleIndex.set(identity.role, identity.index);
  }

  await metadataService.saveProperty(
    routeParams.value.client,
    routeParams.value.brand,
    routeParams.value.casename,
    __props.file.fileReference,
    "dialogueIdentities",
    { dialogueIdentities: JSON.stringify(newDialogueIdentities) },
  );

  await useMetadataStore().refresh();

  reset();
}

function reset(): void {
  selectedRole.value = "";
  name.value = "";
  selectedSpeaker.value = "";
}
</script>
<template>
  <div class="tw-size-full tw-grow tw-overflow-auto tw-bg-gray-100 tw-p-2">
    <RsLoading
      v-if="transcript == null || transcript.status === 'loading'"
      id="dialogueIdentities"
      message="Loading..."
    />
    <h1 v-else-if="transcript.speakers.length === 0">There are no speakers in this transcript.</h1>
    <template v-else>
      <label class="tw-form-label" for="speaker">Speaker</label>
      <select id="speaker" v-model="selectedSpeaker" class="tw-form-select" name="speaker">
        <option value="">Please select ...</option>
        <option v-for="speaker in transcript.speakers" :key="speaker" :value="speaker">Speaker {{ speaker }}</option>
      </select>
      <div class="tw-mt-2 tw-grid tw-grid-cols-2 tw-gap-4">
        <div>
          <label for="role">Role</label>
          <select v-model="selectedRole" class="tw-form-select" :disabled="!selectedSpeaker" name="role">
            <option value="">Please select ...</option>
            <option v-for="dialogueRole in dialogueRoles" :key="dialogueRole" :value="dialogueRole">
              {{ dialogueRole }}
            </option>
          </select>
        </div>
        <div>
          <label for="name">Name</label>
          <input
            v-model.trim="name"
            class="tw-form-input"
            type="text"
            :disabled="!selectedSpeaker || !selectedRole"
            name="name"
          />
        </div>
      </div>
      <div class="tw-my-2 tw-grid tw-w-full tw-grid-cols-3">
        <div class="tw-col-span-2">
          <button
            class="tw-bg-gray active tw-b-r tw-mr-2 tw-rounded-md tw-px-2 tw-py-1 tw-text-xs"
            :class="{ 'tw-opacity-40': !selectedSpeaker || !selectedRole }"
            :disabled="!selectedSpeaker || !selectedRole"
            @click="saveIdentity"
          >
            Save Identity
          </button>
          <button
            class="tw-b-r tw-btn-md-white tw-mr-1 tw-rounded-md"
            :class="{ 'tw-opacity-40': !selectedSpeaker || !selectedRole }"
            :disabled="!selectedSpeaker || !selectedRole"
            @click.prevent="reset"
          >
            Reset
          </button>
        </div>
        <div class="tw-text-right">
          <button
            class="tw-b-r tw-btn-md tw-mr-1 tw-rounded-md tw-bg-red-500 tw-text-white"
            :class="{ 'tw-opacity-40': !identityExists }"
            :disabled="!identityExists"
            @click.prevent="deleteIdentity"
          >
            Delete Identity
          </button>
        </div>
      </div>
    </template>
  </div>
</template>
